import React, { useState, useEffect } from "react";
import { 
  FiShield, 
  FiCheck, 
  FiX, 
  FiSave,
  FiAlertCircle,
  FiList,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiEdit,
  FiTrash2,
  FiUsers,
  FiBell,
  FiMessageSquare,
  FiSettings,
  FiBarChart2,
  FiUserPlus,
  FiClipboard
} from "react-icons/fi";

const CreateCustomRole = () => {
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [existingRoles, setExistingRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [mode, setMode] = useState("create"); // "create" or "edit"
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const [newRole, setNewRole] = useState({
    name: "",
    permissions: []
  });

  // FIXED: Use environment variable instead of hardcoded URL
  const SERVER_URL = process.env.REACT_APP_API_URL || "https://api.tinubu-ambode.org";

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch all available permissions and existing roles
  const fetchAllPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error("No authentication token found");
      }

      console.log("🔍 Fetching permissions from:", `${SERVER_URL}/api/v1/admin/permissions`);

      // FIXED: First try to fetch permissions directly
      const permissionsResponse = await fetch(`${SERVER_URL}/api/v1/admin/permissions`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (permissionsResponse.ok) {
        const permissionsResult = await permissionsResponse.json();
        
        if (permissionsResult.success) {
          const permissionsData = Array.isArray(permissionsResult.data) 
            ? permissionsResult.data 
            : permissionsResult.data?.permissions || [];
          
          setAllPermissions(permissionsData.map(p => p.name || p.permission || p).sort());
          
          // Initialize all categories as expanded
          const categories = groupPermissions(permissionsData.map(p => p.name || p.permission || p));
          const initialExpandedState = {};
          Object.keys(categories).forEach(category => {
            initialExpandedState[category] = true;
          });
          setExpandedCategories(initialExpandedState);
        }
      }

      // Then fetch roles
      const rolesResponse = await fetch(`${SERVER_URL}/api/v1/admin/roles`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!rolesResponse.ok) {
        throw new Error(`HTTP error! status: ${rolesResponse.status}`);
      }

      const rolesResult = await rolesResponse.json();
      
      if (rolesResult.success) {
        setExistingRoles(rolesResult.data || []);
        
        // If permissions weren't loaded from permissions endpoint, extract from roles
        if (allPermissions.length === 0) {
          const allPerms = new Set();
          rolesResult.data.forEach(role => {
            if (role.permissions && Array.isArray(role.permissions)) {
              role.permissions.forEach(perm => {
                if (typeof perm === 'string') {
                  allPerms.add(perm);
                } else if (perm.name || perm.permission) {
                  allPerms.add(perm.name || perm.permission);
                }
              });
            }
          });
          setAllPermissions(Array.from(allPerms).sort());
        }
      } else {
        throw new Error(rolesResult.message || "Failed to fetch data");
      }
    } catch (err) {
      console.error("❌ Error fetching data:", err);
      setError(err.message || "Failed to load data. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllPermissions();
  }, []);

  // Create new role
  const createRole = async () => {
    try {
      setActionLoading(true);
      setApiError("");
      setSuccessMessage("");
      
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error("No authentication token found");
      }

      if (!newRole.name.trim()) {
        throw new Error("Role name is required");
      }

      if (newRole.permissions.length === 0) {
        throw new Error("At least one permission is required");
      }

      const formattedRoleName = newRole.name.toLowerCase().replace(/\s+/g, '-');

      const payload = {
        name: formattedRoleName,
        permissions: newRole.permissions
      };

      console.log("📤 Creating role at:", `${SERVER_URL}/api/v1/admin/roles`);
      console.log("Payload:", payload);

      const response = await fetch(`${SERVER_URL}/api/v1/admin/roles`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.status === 422) {
        const validationErrors = result.errors || {};
        let errorMessage = "Validation failed: ";
        
        Object.keys(validationErrors).forEach(field => {
          errorMessage += `${field}: ${validationErrors[field].join(', ')}. `;
        });
        
        throw new Error(errorMessage);
      }

      if (!response.ok) {
        throw new Error(result.message || `HTTP error! status: ${response.status}`);
      }

      if (result.success) {
        setSuccessMessage(`Role "${formattedRoleName}" created successfully with ${newRole.permissions.length} permissions!`);
        setNewRole({ name: "", permissions: [] });
        fetchAllPermissions();
        setMode("create");
      } else {
        throw new Error(result.message || "Failed to create role");
      }
    } catch (err) {
      console.error("❌ Error creating role:", err);
      setApiError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Update existing role
  const updateRole = async () => {
    try {
      setActionLoading(true);
      setApiError("");
      setSuccessMessage("");
      
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error("No authentication token found");
      }

      if (!newRole.name.trim()) {
        throw new Error("Role name is required");
      }

      if (newRole.permissions.length === 0) {
        throw new Error("At least one permission is required");
      }

      const payload = {
        name: newRole.name,
        permissions: newRole.permissions
      };

      console.log("📤 Updating role at:", `${SERVER_URL}/api/v1/admin/roles/${selectedRole.id}`);

      const response = await fetch(`${SERVER_URL}/api/v1/admin/roles/${selectedRole.id}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.status === 422) {
        const validationErrors = result.errors || {};
        let errorMessage = "Validation failed: ";
        
        Object.keys(validationErrors).forEach(field => {
          errorMessage += `${field}: ${validationErrors[field].join(', ')}. `;
        });
        
        throw new Error(errorMessage);
      }

      if (!response.ok) {
        throw new Error(result.message || `HTTP error! status: ${response.status}`);
      }

      if (result.success) {
        setSuccessMessage(`Role "${newRole.name}" updated successfully with ${newRole.permissions.length} permissions!`);
        setNewRole({ name: "", permissions: [] });
        setSelectedRole(null);
        fetchAllPermissions();
        setMode("create");
      } else {
        throw new Error(result.message || "Failed to update role");
      }
    } catch (err) {
      console.error("❌ Error updating role:", err);
      setApiError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Delete role
  const deleteRole = async (roleId, roleName) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error("No authentication token found");
      }

      console.log("🗑️ Deleting role at:", `${SERVER_URL}/api/v1/admin/roles/${roleId}`);

      const response = await fetch(`${SERVER_URL}/api/v1/admin/roles/${roleId}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || `HTTP error! status: ${response.status}`);
      }

      if (result.success) {
        setSuccessMessage(`Role "${roleName}" deleted successfully!`);
        fetchAllPermissions();
        if (selectedRole && selectedRole.id === roleId) {
          setSelectedRole(null);
          setMode("create");
          setNewRole({ name: "", permissions: [] });
        }
        setShowDeleteModal(false);
        setRoleToDelete(null);
      } else {
        throw new Error(result.message || "Failed to delete role");
      }
    } catch (err) {
      console.error("❌ Error deleting role:", err);
      setApiError(err.message);
      setShowDeleteModal(false);
      setRoleToDelete(null);
    }
  };

  // Edit existing role
  const editRole = (role) => {
    setSelectedRole(role);
    setNewRole({
      name: role.name,
      permissions: [...(role.permissions || [])]
    });
    setMode("edit");
    setApiError("");
    setSuccessMessage("");
  };

  // Show delete confirmation modal
  const confirmDeleteRole = (roleId, roleName) => {
    setRoleToDelete({ id: roleId, name: roleName });
    setShowDeleteModal(true);
  };

  // Cancel delete operation
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setRoleToDelete(null);
  };

  // Group permissions by category with better icons
  const groupPermissions = (permissions) => {
    const groups = {
      "Member Management": [],
      "Broadcast Announcements": [],
      "Broadcast Requests": [],
      "Admin Management": [],
      "Group Chat": [],
      "Reports": [],
      "System Management": [],
      "Other": []
    };

    permissions.forEach(permission => {
      const permString = typeof permission === 'string' ? permission : (permission.name || permission.permission || '');
      
      if (permString.includes('member')) {
        groups["Member Management"].push(permString);
      } else if (permString.includes('broadcast-announcement')) {
        groups["Broadcast Announcements"].push(permString);
      } else if (permString.includes('broadcast-request')) {
        groups["Broadcast Requests"].push(permString);
      } else if (permString.includes('admin') || permString.includes('create-')) {
        groups["Admin Management"].push(permString);
      } else if (permString.includes('group-chat')) {
        groups["Group Chat"].push(permString);
      } else if (permString.includes('report') || permString.includes('view-')) {
        groups["Reports"].push(permString);
      } else if (permString.includes('manage-') || permString.includes('system') || permString.includes('upload') || permString.includes('theme')) {
        groups["System Management"].push(permString);
      } else {
        groups["Other"].push(permString);
      }
    });

    // Remove empty groups
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) {
        delete groups[key];
      }
    });

    return groups;
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    switch (category) {
      case "Member Management":
        return <FiUsers style={styles.categoryIcon} />;
      case "Broadcast Announcements":
        return <FiBell style={styles.categoryIcon} />;
      case "Broadcast Requests":
        return <FiClipboard style={styles.categoryIcon} />;
      case "Admin Management":
        return <FiUserPlus style={styles.categoryIcon} />;
      case "Group Chat":
        return <FiMessageSquare style={styles.categoryIcon} />;
      case "Reports":
        return <FiBarChart2 style={styles.categoryIcon} />;
      case "System Management":
        return <FiSettings style={styles.categoryIcon} />;
      default:
        return <FiList style={styles.categoryIcon} />;
    }
  };

  // Format permission for display
  const formatPermission = (permission) => {
    const permString = typeof permission === 'string' ? permission : (permission.name || permission.permission || '');
    return permString.split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Handle permission selection
  const togglePermission = (permission) => {
    setNewRole(prev => {
      const currentPermissions = prev.permissions;
      if (currentPermissions.includes(permission)) {
        return {
          ...prev,
          permissions: currentPermissions.filter(p => p !== permission)
        };
      } else {
        return {
          ...prev,
          permissions: [...currentPermissions, permission]
        };
      }
    });
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Reset form
  const resetForm = () => {
    setNewRole({ name: "", permissions: [] });
    setSelectedRole(null);
    setMode("create");
    setApiError("");
    setSuccessMessage("");
    setSearchTerm("");
  };

  // Filter permissions based on search
  const getFilteredPermissions = () => {
    const grouped = groupPermissions(allPermissions);
    const filteredGroups = {};
    
    Object.keys(grouped).forEach(category => {
      const filteredPermissions = grouped[category].filter(permission => {
        const permString = typeof permission === 'string' ? permission : (permission.name || permission.permission || '');
        return permString.toLowerCase().includes(searchTerm.toLowerCase()) ||
               formatPermission(permission).toLowerCase().includes(searchTerm.toLowerCase())
      });
      
      if (filteredPermissions.length > 0) {
        filteredGroups[category] = filteredPermissions;
      }
    });
    
    return filteredGroups;
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner}></div>
          <p style={styles.loadingText}>Loading permissions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.errorContainer}>
          <FiAlertCircle style={styles.errorIcon} />
          <h3 style={styles.errorTitle}>Error Loading Permissions</h3>
          <p style={styles.errorText}>{error}</p>
          <button onClick={fetchAllPermissions} style={styles.retryButton}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const permissionGroups = getFilteredPermissions();
  const totalSelected = newRole.permissions.length;

  return (
    <div style={styles.page}>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContainer}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Confirm Deletion</h3>
            </div>
            <div style={styles.modalBody}>
              <FiAlertCircle style={styles.modalWarningIcon} />
              <p style={styles.modalText}>
                Are you sure you want to delete the role "<strong>{roleToDelete?.name}</strong>"? 
                This action cannot be undone.
              </p>
            </div>
            <div style={styles.modalActions}>
              <button
                onClick={cancelDelete}
                style={styles.modalCancelButton}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteRole(roleToDelete.id, roleToDelete.name)}
                style={styles.modalDeleteButton}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <>
                    <div style={styles.buttonSpinner}></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <FiTrash2 style={styles.buttonIcon} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerIconContainer}>
            <FiShield style={styles.headerIcon} />
          </div>
          <div style={styles.headerText}>
            <h1 style={styles.pageTitle}>
              {mode === "create" ? "Create Custom Role" : `Edit Role: ${selectedRole?.name}`}
            </h1>
            <p style={styles.pageSubtitle}>
              {mode === "create" 
                ? "Define a new role with specific system permissions" 
                : "Update role permissions and information"}
            </p>
          </div>
        </div>
        {!isMobile && (
          <div style={styles.headerStats}>
            <div style={styles.statCard}>
              <div style={styles.statIconContainer}>
                <FiList style={styles.statIcon} />
              </div>
              <div style={styles.statText}>
                <div style={styles.statNumber}>{allPermissions.length}</div>
                <div style={styles.statLabel}>Available Permissions</div>
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statIconContainer}>
                <FiShield style={styles.statIcon} />
              </div>
              <div style={styles.statText}>
                <div style={styles.statNumber}>{existingRoles.length}</div>
                <div style={styles.statLabel}>Existing Roles</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Stats */}
      {isMobile && (
        <div style={styles.mobileStats}>
          <div style={styles.mobileStat}>
            <FiList style={styles.mobileStatIcon} />
            <span style={styles.mobileStatText}>{allPermissions.length} Permissions</span>
          </div>
          <div style={styles.mobileStat}>
            <FiShield style={styles.mobileStatIcon} />
            <span style={styles.mobileStatText}>{existingRoles.length} Roles</span>
          </div>
        </div>
      )}

      {/* Alert Messages */}
      {successMessage && (
        <div style={styles.alertSuccess}>
          <FiCheck style={styles.alertIcon} />
          <div style={styles.alertContent}>
            <div style={styles.alertTitle}>Success</div>
            <div style={styles.alertMessage}>{successMessage}</div>
          </div>
        </div>
      )}

      {apiError && (
        <div style={styles.alertError}>
          <FiAlertCircle style={styles.alertIcon} />
          <div style={styles.alertContent}>
            <div style={styles.alertTitle}>Error</div>
            <div style={styles.alertMessage}>{apiError}</div>
          </div>
        </div>
      )}

      <div style={styles.content}>
        {/* Existing Roles Card */}
        {existingRoles.length > 0 && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.cardHeaderLeft}>
                <FiList style={styles.cardIcon} />
                <h2 style={styles.cardTitle}>Existing Roles</h2>
              </div>
            </div>
            <div style={styles.cardBody}>
              <div style={isMobile ? styles.rolesGridMobile : styles.rolesGrid}>
                {existingRoles.map((role) => (
                  <div key={role.id} style={styles.roleItem}>
                    <div style={styles.roleItemHeader}>
                      <div style={styles.roleItemInfo}>
                        <h4 style={styles.roleItemName}>{role.name}</h4>
                        <span style={styles.roleItemPermissions}>
                          {role.permissions?.length || 0} permissions
                        </span>
                      </div>
                      <div style={styles.roleItemActions}>
                        <button
                          onClick={() => editRole(role)}
                          style={styles.editButton}
                          title="Edit Role"
                        >
                          <FiEdit size={14} />
                        </button>
                        <button
                          onClick={() => confirmDeleteRole(role.id, role.name)}
                          style={styles.deleteButton}
                          title="Delete Role"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div style={styles.roleItemPermissionsList}>
                      {role.permissions?.slice(0, isMobile ? 2 : 3).map((permission, index) => (
                        <span key={index} style={styles.permissionTag}>
                          {formatPermission(permission)}
                        </span>
                      ))}
                      {role.permissions?.length > (isMobile ? 2 : 3) && (
                        <span style={styles.morePermissions}>
                          +{role.permissions.length - (isMobile ? 2 : 3)} more
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Role Information Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.cardHeaderLeft}>
              <FiShield style={styles.cardIcon} />
              <h2 style={styles.cardTitle}>Role Information</h2>
            </div>
            {mode === "edit" && (
              <button
                onClick={resetForm}
                style={styles.cancelEditButton}
              >
                <FiX size={14} />
                Cancel
              </button>
            )}
          </div>
          <div style={styles.cardBody}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Role Name <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={newRole.name}
                onChange={(e) => setNewRole({...newRole, name: e.target.value})}
                placeholder="e.g., regional-coordinator"
                style={styles.textInput}
                disabled={mode === "edit"}
              />
              <div style={styles.helpText}>
                {mode === "create" 
                  ? "Use lowercase letters and hyphens. This name must be unique across the system."
                  : "Role name cannot be changed when editing."}
              </div>
            </div>

            <div style={styles.selectionSummary}>
              <div style={styles.summaryIcon}>
                <FiCheck style={styles.summaryIconSvg} />
              </div>
              <div style={styles.summaryText}>
                <div style={styles.summaryCount}>{totalSelected}</div>
                <div style={styles.summaryLabel}>permissions selected</div>
              </div>
            </div>
          </div>
        </div>

        {/* Permissions Selection Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.cardHeaderLeft}>
              <FiList style={styles.cardIcon} />
              <h2 style={styles.cardTitle}>Permission Selection</h2>
            </div>
          </div>

          <div style={styles.cardBody}>
            {/* Search */}
            <div style={styles.searchContainer}>
              <FiSearch style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search permissions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
            </div>

            {/* Permissions Grid */}
            <div style={styles.permissionsContainer}>
              {Object.keys(permissionGroups).length === 0 ? (
                <div style={styles.emptyState}>
                  <FiSearch style={styles.emptyIcon} />
                  <div style={styles.emptyText}>
                    No permissions found matching your search
                  </div>
                </div>
              ) : (
                Object.entries(permissionGroups).map(([category, permissions]) => (
                  <div key={category} style={styles.permissionCategory}>
                    <div 
                      style={styles.categoryHeader}
                      onClick={() => toggleCategory(category)}
                    >
                      <div style={styles.categoryTitleSection}>
                        {getCategoryIcon(category)}
                        <h3 style={styles.categoryTitle}>{category}</h3>
                        <div style={styles.categoryStats}>
                          <span style={styles.categoryCount}>
                            {permissions.filter(p => newRole.permissions.includes(p)).length} of {permissions.length} selected
                          </span>
                        </div>
                      </div>
                      <div style={styles.categoryToggle}>
                        {expandedCategories[category] ? <FiChevronUp /> : <FiChevronDown />}
                      </div>
                    </div>

                    {expandedCategories[category] && (
                      <div style={isMobile ? styles.permissionsGridMobile : styles.permissionsGrid}>
                        {permissions.map((permission, index) => (
                          <div
                            key={index}
                            style={{
                              ...styles.permissionItem,
                              ...(newRole.permissions.includes(permission) ? styles.permissionItemSelected : {})
                            }}
                            onClick={() => togglePermission(permission)}
                          >
                            <div style={{
                              ...styles.permissionCheckbox,
                              ...(newRole.permissions.includes(permission) && styles.permissionCheckboxSelected)
                            }}>
                              {newRole.permissions.includes(permission) && (
                                <FiCheck style={styles.permissionCheckIcon} />
                              )}
                            </div>
                            <div style={styles.permissionText}>
                              {formatPermission(permission)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={styles.actionSection}>
          <button
            onClick={resetForm}
            style={styles.secondaryButton}
            disabled={actionLoading}
          >
            <FiX size={14} />
            {mode === "create" ? "Reset" : "Cancel"}
          </button>
          <div style={styles.primaryActions}>
            {mode === "edit" && (
              <button
                onClick={() => confirmDeleteRole(selectedRole.id, selectedRole.name)}
                style={styles.dangerButton}
                disabled={actionLoading}
              >
                <FiTrash2 size={14} />
                Delete
              </button>
            )}
            <button
              onClick={mode === "create" ? createRole : updateRole}
              disabled={!newRole.name.trim() || totalSelected === 0 || actionLoading}
              style={{
                ...styles.primaryButton,
                ...((!newRole.name.trim() || totalSelected === 0 || actionLoading) && styles.primaryButtonDisabled)
              }}
            >
              {actionLoading ? (
                <>
                  <div style={styles.buttonSpinner}></div>
                  {mode === "create" ? "Creating..." : "Updating..."}
                </>
              ) : (
                <>
                  <FiSave size={14} />
                  {mode === "create" ? "Create Role" : "Update Role"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    overflowX: 'hidden',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '60vh',
    padding: '40px',
  },
  loadingSpinner: {
    width: '48px',
    height: '48px',
    border: '3px solid #e5e7eb',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px',
  },
  loadingText: {
    fontSize: '16px',
    color: '#6b7280',
    margin: 0,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '60vh',
    padding: '40px',
    textAlign: 'center',
  },
  errorIcon: {
    fontSize: '64px',
    color: '#dc2626',
    marginBottom: '20px',
  },
  errorTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1f2937',
    margin: '0 0 12px 0',
  },
  errorText: {
    fontSize: '16px',
    color: '#6b7280',
    margin: '0 0 24px 0',
    lineHeight: '1.5',
  },
  retryButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    minWidth: '100px',
  },
  // Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modalContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    maxWidth: '400px',
    width: '100%',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
  },
  modalHeader: {
    marginBottom: '12px',
  },
  modalTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937',
    margin: 0,
  },
  modalBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '20px',
  },
  modalWarningIcon: {
    fontSize: '40px',
    color: '#dc2626',
    marginBottom: '12px',
  },
  modalText: {
    fontSize: '14px',
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: '1.4',
    margin: 0,
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
  },
  modalCancelButton: {
    backgroundColor: 'white',
    color: '#374151',
    border: '1px solid #d1d5db',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '70px',
    whiteSpace: 'nowrap',
  },
  modalDeleteButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    minWidth: '90px',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
  },
  header: {
    backgroundColor: 'white',
    borderBottom: '1px solid #e5e7eb',
    padding: '20px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    minWidth: '300px',
  },
  headerIconContainer: {
    width: '48px',
    height: '48px',
    backgroundColor: '#f0f9ff',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerIcon: {
    fontSize: '20px',
    color: '#3b82f6',
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  pageTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1f2937',
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  pageSubtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: 0,
    lineHeight: '1.4',
    wordBreak: 'break-word',
  },
  headerStats: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: '#f8fafc',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    minWidth: '140px',
  },
  statIconContainer: {
    width: '32px',
    height: '32px',
    backgroundColor: '#3b82f6',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  statIcon: {
    fontSize: '16px',
    color: 'white',
  },
  statText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  statNumber: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1f2937',
    lineHeight: '1',
    whiteSpace: 'nowrap',
  },
  statLabel: {
    fontSize: '11px',
    color: '#6b7280',
    marginTop: '2px',
    whiteSpace: 'nowrap',
  },
  // Mobile Stats
  mobileStats: {
    display: 'flex',
    justifyContent: 'space-around',
    backgroundColor: 'white',
    borderBottom: '1px solid #e5e7eb',
    padding: '12px 20px',
    gap: '10px',
  },
  mobileStat: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flex: 1,
    justifyContent: 'center',
  },
  mobileStatIcon: {
    fontSize: '14px',
    color: '#3b82f6',
  },
  mobileStatText: {
    fontSize: '12px',
    color: '#374151',
    fontWeight: '500',
  },
  alertSuccess: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    color: '#166534',
    padding: '12px 16px',
    margin: '0 24px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginTop: '20px',
  },
  alertError: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '12px 16px',
    margin: '0 24px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginTop: '20px',
  },
  alertIcon: {
    fontSize: '16px',
    marginTop: '1px',
    flexShrink: 0,
  },
  alertContent: {
    flex: 1,
    minWidth: 0,
  },
  alertTitle: {
    fontSize: '13px',
    fontWeight: '600',
    marginBottom: '2px',
  },
  alertMessage: {
    fontSize: '13px',
    lineHeight: '1.4',
    wordBreak: 'break-word',
  },
  content: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '10px',
    border: '1px solid #e5e7eb',
    marginBottom: '20px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  },
  cardHeader: {
    padding: '20px',
    borderBottom: '1px solid #f3f4f6',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
  },
  cardHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 1,
    minWidth: 0,
  },
  cardIcon: {
    fontSize: '16px',
    color: '#3b82f6',
    flexShrink: 0,
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937',
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cancelEditButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    padding: '5px 10px',
    borderRadius: '5px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    minWidth: 'auto',
    width: 'auto',
  },
  cardBody: {
    padding: '20px',
  },
  rolesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '12px',
  },
  rolesGridMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  roleItem: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '14px',
    backgroundColor: '#f8fafc',
    minWidth: 0,
  },
  roleItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '10px',
    gap: '8px',
  },
  roleItemInfo: {
    flex: 1,
    minWidth: 0,
  },
  roleItemName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1f2937',
    margin: '0 0 3px 0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  roleItemPermissions: {
    fontSize: '11px',
    color: '#6b7280',
    whiteSpace: 'nowrap',
  },
  roleItemActions: {
    display: 'flex',
    gap: '4px',
    flexShrink: 0,
  },
  editButton: {
    backgroundColor: 'transparent',
    color: '#3b82f6',
    border: '1px solid #3b82f6',
    padding: '3px 5px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '11px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '26px',
    height: '26px',
    flexShrink: 0,
  },
  deleteButton: {
    backgroundColor: 'transparent',
    color: '#dc2626',
    border: '1px solid #dc2626',
    padding: '3px 5px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '11px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '26px',
    height: '26px',
    flexShrink: 0,
  },
  roleItemPermissionsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    alignItems: 'center',
  },
  permissionTag: {
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '3px',
    padding: '1px 5px',
    fontSize: '10px',
    color: '#374151',
    whiteSpace: 'nowrap',
    maxWidth: '100px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  morePermissions: {
    fontSize: '10px',
    color: '#6b7280',
    fontStyle: 'italic',
    whiteSpace: 'nowrap',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
    whiteSpace: 'nowrap',
  },
  required: {
    color: '#dc2626',
  },
  textInput: {
    width: '100%',
    maxWidth: '300px',
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: '5px',
    fontSize: '13px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  helpText: {
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '4px',
    maxWidth: '400px',
  },
  selectionSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: '#f0f9ff',
    padding: '12px',
    borderRadius: '6px',
    border: '1px solid #bae6fd',
    maxWidth: '200px',
  },
  summaryIcon: {
    width: '24px',
    height: '24px',
    backgroundColor: '#3b82f6',
    borderRadius: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  summaryIconSvg: {
    fontSize: '12px',
    color: 'white',
  },
  summaryText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  summaryCount: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#0369a1',
    lineHeight: '1',
    whiteSpace: 'nowrap',
  },
  summaryLabel: {
    fontSize: '11px',
    color: '#6b7280',
    marginTop: '1px',
    whiteSpace: 'nowrap',
  },
  searchContainer: {
    position: 'relative',
    marginBottom: '20px',
    maxWidth: '300px',
  },
  searchIcon: {
    position: 'absolute',
    left: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9ca3af',
    fontSize: '13px',
  },
  searchInput: {
    width: '100%',
    padding: '8px 10px 8px 30px',
    border: '1px solid #d1d5db',
    borderRadius: '5px',
    fontSize: '13px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  permissionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '30px 16px',
    color: '#6b7280',
  },
  emptyIcon: {
    fontSize: '40px',
    marginBottom: '12px',
    opacity: 0.5,
  },
  emptyText: {
    fontSize: '13px',
    textAlign: 'center',
  },
  permissionCategory: {
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  categoryHeader: {
    backgroundColor: '#f8fafc',
    padding: '12px 14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  categoryTitleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    minWidth: 0,
  },
  categoryIcon: {
    color: '#3b82f6',
    fontSize: '14px',
    flexShrink: 0,
  },
  categoryTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    margin: 0,
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  categoryStats: {
    fontSize: '11px',
    color: '#6b7280',
    flexShrink: 0,
    marginRight: '8px',
  },
  categoryCount: {
    fontWeight: '500',
    whiteSpace: 'nowrap',
  },
  categoryToggle: {
    color: '#6b7280',
    fontSize: '13px',
    flexShrink: 0,
  },
  permissionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: '6px',
    padding: '14px',
    backgroundColor: 'white',
  },
  permissionsGridMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '14px',
    backgroundColor: 'white',
  },
  permissionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 10px',
    border: '1px solid #e5e7eb',
    borderRadius: '5px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: 'white',
    minWidth: 0,
  },
  permissionItemSelected: {
    backgroundColor: '#f0f9ff',
    borderColor: '#3b82f6',
  },
  permissionCheckbox: {
    width: '12px',
    height: '12px',
    border: '2px solid #d1d5db',
    borderRadius: '2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    flexShrink: 0,
  },
  permissionCheckboxSelected: {
    backgroundColor: '#3b82f6',
    borderColor: '#3b82f6',
  },
  permissionCheckIcon: {
    fontSize: '8px',
    color: 'white',
  },
  permissionText: {
    fontSize: '11px',
    color: '#374151',
    fontWeight: '500',
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Action Buttons
  actionSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0',
    flexWrap: 'wrap',
    gap: '10px',
  },
  secondaryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: 'white',
    color: '#374151',
    border: '1px solid #d1d5db',
    padding: '6px 12px',
    borderRadius: '5px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '70px',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    width: 'auto',
  },
  primaryActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  dangerButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '5px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    minWidth: '70px',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    width: 'auto',
  },
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '5px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    minWidth: '100px',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    width: 'auto',
  },
  primaryButtonDisabled: {
    backgroundColor: '#9ca3af',
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  buttonIcon: {
    fontSize: '12px',
  },
  buttonSpinner: {
    width: '12px',
    height: '12px',
    border: '2px solid transparent',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

// Add CSS animation for loading spinner
const styleSheet = document.styleSheets[0];
styleSheet.insertRule(`
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`, styleSheet.cssRules.length);

export default CreateCustomRole;